import { useLayoutEffect, useRef } from 'react';

// Scale the complete card and queue together rather than clipping actions below the fold.
export function useArenaFit<T extends HTMLElement = HTMLElement>(
  hasCard: boolean,
  explore = false,
) {
  const screen = useRef<T>(null);
  useLayoutEffect(() => {
    const node = screen.current;
    if (
      !node ||
      (!explore && !node.parentElement?.classList.contains('has-arena'))
    )
      return;
    const chrome = explore
      ? Array.from(
          node
            .closest('.arena-app')
            ?.querySelectorAll<HTMLElement>(
              ':scope > .top-bar, :scope > .game-toolbar, :scope > .app-notices',
            ) ?? [],
        )
      : [];
    const stage = node.querySelector<HTMLElement>('.arena-stage');
    const rails = Array.from(node.querySelectorAll<HTMLElement>('.token-rail'));
    const parts = stage ? [stage, ...rails] : rails;
    const desktop = window.matchMedia('(min-width: 1181px)');
    const fit = () => {
      if (explore) {
        // Use the same available viewport as Arena, including its hidden toolbar.
        node.style.top = desktop.matches
          ? chrome.reduce(
              (height, part) => height + part.getBoundingClientRect().height,
              0,
            ) + 'px'
          : '';
      }
      const style = getComputedStyle(node);
      const available =
        node.clientHeight -
        parseFloat(style.paddingTop) -
        parseFloat(style.paddingBottom);
      for (const part of parts) {
        const natural = Math.max(part.scrollHeight, part.offsetHeight);
        const scale =
          desktop.matches &&
          available > 0 &&
          natural > 0 &&
          (part !== stage || hasCard)
            ? Math.min(1, available / natural)
            : 1;
        const zoom = String(Math.floor(scale * 1000) / 1000);
        if (part.style.zoom !== zoom) part.style.zoom = zoom;
      }
    };
    let frame = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(fit);
    });
    observer.observe(node);
    for (const part of chrome) observer.observe(part);
    for (const part of parts) observer.observe(part);
    desktop.addEventListener('change', fit);
    fit();
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
      desktop.removeEventListener('change', fit);
      for (const part of parts) part.style.removeProperty('zoom');
    };
  }, [hasCard, explore]);
  return screen;
}
